import { useRouter } from 'next/navigation'
import { useCallback } from 'react'

import configuration from '~/configuration'
import { Skeleton } from '~/core/ui/Skeleton'

import useUniversalSearch from '~/lib/features/universal-search/hooks/useUniversalSearch'
import type { TypeUniversalSearchItem } from '~/lib/features/universal-search/types'
import { ENUM_SECTION_USEARCH_KEYS } from '~/lib/features/universal-search/utilities/enum'
import { handleKeyboardNavigation } from '~/lib/features/universal-search/utilities/keyboard-navigation'

import SearchFooter from './USearchFooter'
import SearchHeader from './USearchHeader'
import SearchResults from './USearchResults'

export const USearchView = ({ onClose }: { onClose: () => void }) => {
  const router = useRouter()
  const {
    searchTerm,
    setSearchTerm,
    results,
    flatResults,
    selectedIndex,
    setSelectedIndex,
    isLoading,
    debouncedSearch,
    isKeyboardNavigation,
    setIsKeyboardNavigation
  } = useUniversalSearch()
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    debouncedSearch(e.target.value)
  }

  const onClearInput = () => {
    setSearchTerm('')
    debouncedSearch('')
  }

  const handleSelect = useCallback(
    (item: TypeUniversalSearchItem) => {
      if (item.sectionKey === ENUM_SECTION_USEARCH_KEYS.JOBS) {
        router.push(configuration.path.jobs.detail(item.id))
      } else if (item.sectionKey === ENUM_SECTION_USEARCH_KEYS.COMPANIES) {
        router.push(configuration.path.agency.companyDetail(Number(item.id)))
      } else if (item.sectionKey === ENUM_SECTION_USEARCH_KEYS.PROFILES) {
        router.push(configuration.path.candidates.detail(item.id))
      }
      onClose()
    },
    [onClose, flatResults, selectedIndex, setSelectedIndex, isKeyboardNavigation]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const modalContent = document.querySelector('.modal-universal-search-content')
      handleKeyboardNavigation(
        e,
        modalContent,
        flatResults,
        selectedIndex,
        setSelectedIndex,
        onClose,
        handleSelect,
        setIsKeyboardNavigation
      )
    },
    [flatResults, selectedIndex, setSelectedIndex, onClose, handleSelect]
  )

  return (
    <div
      className="z-1000 h-[488px] w-[680px] overflow-hidden rounded-lg bg-white shadow-xl"
      onKeyDown={handleKeyDown}>
      <SearchHeader
        onClearInput={onClearInput}
        searchTerm={searchTerm}
        onChange={handleInputChange}
      />

      {isLoading ? (
        <div className="flex h-[calc(488px-88px)] w-full">
          <div className="px-3 py-2">
            {[1, 2].map((index) => (
              <div key={index} className="mb-4 flex flex-col space-y-3">
                <Skeleton className="h-3 w-10" />
                <Skeleton className="h-4 w-[650px]" />
                <Skeleton className="h-4 w-[650px]" />
                <Skeleton className="h-4 w-[650px]" />
                <Skeleton className="h-4 w-[650px]" />
                <Skeleton className="h-4 w-[650px]" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <SearchResults
          searchTerm={searchTerm}
          results={results}
          flatResults={flatResults}
          selectedIndex={selectedIndex}
          onItemSelect={handleSelect}
          onItemHover={setSelectedIndex}
          isKeyboardNavigation={isKeyboardNavigation}
          setIsKeyboardNavigation={setIsKeyboardNavigation}
        />
      )}

      <SearchFooter />
    </div>
  )
}
